import Head from 'next/head'
import { useRouter } from 'next/router'
import { Venue, VenueEvent } from 'types/model'

import { formatUTCDate } from 'shared/src/utils/dateUtils'
import { imageOptimize } from '@utils/images'
import { env } from '@lib/env'
import { useMemo } from 'react'

export function formatEventDiaryDateForMeta(
  date: string | number | Date | null | undefined
): string {
  if (!date) return ''

  return formatUTCDate({
    date: new Date(date),
    formatStr: 'EEE MMM d yyyy',
  })
}

export function VenueEventMeta({
  venue,
  event,
  titlePrefix,
}: {
  venue: Venue
  event?: VenueEvent | null
  titlePrefix?: string
}) {
  const router = useRouter()
  const pageTitle = `${titlePrefix ?? ''}${event ? `${event.name} | ` : ''}${venue.name}`
  const description = `Book a ticket or table today for ${(event ?? venue).name}`
  const image = useMemo(
    () =>
      imageOptimize((event ?? venue).primaryImage?.filename, {
        w: '1200',
        h: '627',
        q: '50',
        fm: 'webp',
        fit: 'fill',
        fill: 'blur',
      }),
    [event, venue]
  )

  const keywords = useMemo(
    () => [
      'Nightlife',
      'Night life',
      'Venue',
      'Late night',
      'Club',
      'Tablelist',
      'Tablelist Pro',
      ...(event
        ? [
            event.name,
            formatEventDiaryDateForMeta(event.date),
          ]
        : []),
      venue.name,
      ...(venue.location.address
        ? [
            venue.location.address.city,
            venue.location.address.state,
            venue.location.address.zip,
          ]
        : []),
    ],
    [event, venue.location.address, venue.name]
  )
  const url = useMemo(
    () => `${env.domain}/${router.asPath.split('?')[0]}`,
    [router.asPath]
  )

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name='description' content={description} />
      <meta name='keywords' content={keywords.join(', ')} />
      <meta name='author' content={venue.name} />

      <meta property='og:type' content='website' />
      <meta property='og:title' content={pageTitle} />
      <meta property='og:description' content={description} />
      <meta property='og:image' content={image} />
      <meta property='og:url' content={url} />

      <meta property='twitter:card' content='summary_large_image' />
      <meta property='twitter:url' content={url} />
      <meta property='twitter:title' content={pageTitle} />
      <meta property='twitter:description' content={description} />
      <meta property='twitter:image' content={image} />
    </Head>
  )
}
